import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from"@/components/ui/tabs";
import { Badge } from"@/components/ui/badge";
import { Plus, Building2, KeyRound, Briefcase, ChevronDown, ChevronRight, Car, Warehouse, Bike } from"lucide-react";
import { toast } from"sonner";
import { useOpenFromQuery } from "@/hooks/useOpenFromQuery";
import EntityDocumentsTab from"@/components/documents/EntityDocumentsTab";
import { BedroomFilter } from"@/components/units/BedroomFilter";
import { getBedroomOptions, matchesBedroomFilter } from"@/components/units/bedroomFilterUtils";

const UnitsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [units, setUnits] = useState([]);
 const [projects, setProjects] = useState([]);
 const [buildings, setBuildings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [editDialogOpen, setEditDialogOpen] = useState(false);
 const [detailOpen, setDetailOpen] = useState(false);
 const [selectedUnit, setSelectedUnit] = useState(null);
 const [unitReservation, setUnitReservation] = useState(null);
 const [loadingReservation, setLoadingReservation] = useState(false);
 const [formData, setFormData] = useState({ project_id:"", building_id:"", code:"", floor: 1, bedrooms: 2, net_m2: 65, unit_type:"residential", floor_plan_url:""});
 const [editFormData, setEditFormData] = useState({ code:"", floor: 1, bedrooms: 2, net_m2: 65, unit_type:"residential"});
 const [selectedProjectForForm, setSelectedProjectForForm] = useState("");
 const [expandedProjects, setExpandedProjects] = useState({});
 const [selectedBedrooms, setSelectedBedrooms] = useState('all');
 const [filteredExpandedProjects, setFilteredExpandedProjects] = useState({});
 // Contractor responses intentionally omit availability; keep that view unchanged.
 const canFilterAvailability = user.role !== 'contractor';
 const bedroomFilterActive = canFilterAvailability && selectedBedrooms !== 'all';
 const filteredUnits = bedroomFilterActive
 ? units.filter(unit => matchesBedroomFilter(unit, selectedBedrooms)) : units;
 const bedroomOptions = getBedroomOptions(units);

 const handleBedroomChange = (value) => {
 setSelectedBedrooms(value);
 setFilteredExpandedProjects({});
 };

 useEffect(() => { loadData(); }, []);
 useOpenFromQuery("unitId", units, (u) => { setSelectedUnit(u); setDetailOpen(true); });

 const loadData = async () => {
 try {
 const [unitsRes, buildingsRes, projectsRes] = await Promise.all([
 axios.get("/units"), 
 axios.get("/buildings"),
 axios.get("/projects")
 ]);
 setUnits(unitsRes.data);
 setBuildings(buildingsRes.data);
 setProjects(projectsRes.data);
 
 // Initialize all projects as collapsed by default
 const expanded = {};
 projectsRes.data.forEach(project => {
 expanded[project.id] = false;
 });
 setExpandedProjects(expanded);
 } catch (error) { 
 toast.error(t("messages.loadFailed")); 
 } finally { 
 setLoading(false); 
 }
 };

 const toggleProject = (projectId) => {
 if (bedroomFilterActive) {
 setFilteredExpandedProjects(prev => ({ ...prev, [projectId]: prev[projectId] === false }));
 return;
 }
 setExpandedProjects(prev => ({
 ...prev,
 [projectId]: !prev[projectId]
 }));
 };

 // Group units by project
 const getUnitsGroupedByProject = () => {
 const grouped = {};
 
 projects.forEach(project => {
 grouped[project.id] = {
 project: project,
 buildings: {}
 };
 });

 buildings.forEach(building => {
 if (grouped[building.project_id]) {
 grouped[building.project_id].buildings[building.id] = {
 building: building,
 units: []
 };
 }
 });

 filteredUnits.forEach(unit => {
 const building = buildings.find(b => b.id === unit.building_id);
 if (building && grouped[building.project_id]) {
 grouped[building.project_id].buildings[building.id].units.push(unit);
 }
 });

 return grouped;
 };

 const groupedUnits = getUnitsGroupedByProject();
 const visibleUnitCount = Object.values(groupedUnits).reduce((total, group) =>
 total + Object.values(group.buildings).reduce((count, building) => count + building.units.length, 0), 0);

 const handleSubmit = async (e) => {
 e.preventDefault();
 try { await axios.post("/units", formData); toast.success(t("messages.saveSuccess")); setDialogOpen(false); setFormData({ building_id:"", code:"", floor: 1, bedrooms: 2, net_m2: 65 }); loadData(); } catch (error) { toast.error(error.response?.data?.detail ||"Failed to create unit"); }
 };

 const openEditDialog = (unit) => {
 setSelectedUnit(unit);
 setEditFormData({
 code: unit.code,
 floor: unit.floor,
 bedrooms: unit.bedrooms,
 net_m2: unit.net_m2,
 unit_type: unit.unit_type
 });
 setEditDialogOpen(true);
 };

 const handleEditSubmit = async (e) => {
 e.preventDefault();
 try {
 await axios.patch(`/units/${selectedUnit.id}`, editFormData);
 toast.success(t("messages.saveSuccess"));
 setEditDialogOpen(false);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to update unit");
 }
 };

 const loadUnitReservation = async (unitId) => {
 setLoadingReservation(true);
 try {
 const response = await axios.get(`/reservations?unit_id=${unitId}`);
 if (response.data && response.data.length > 0) {
 setUnitReservation(response.data[0]);
 } else {
 setUnitReservation(null);
 }
 } catch (error) {
 setUnitReservation(null);
 } finally {
 setLoadingReservation(false);
 }
 };

 const openUnitDetail = (unit) => {
 setSelectedUnit(unit);
 setDetailOpen(true);
 if (unit.status ==="reserved"|| unit.status ==="sold") {
 loadUnitReservation(unit.id);
 }
 };

 const statusColors = { available:"bg-emerald-500/10 text-emerald-700 border-green-200/60", hold:"bg-amber-500/10 text-amber-700 border-orange-200/60", reserved:"bg-blue-500/10 text-blue-700 border-blue-200/60", sold:"bg-white/60 text-slate-800 border-slate-200"};

 return (
 <Layout user={user} onLogout={onLogout}>
 <div data-testid="units-page"className="space-y-6">
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900 mb-1">{t('units.title')}</h1>
 <p className="text-sm text-slate-500">{t('units.subtitle')}</p>
 </div>
 {["owner","management"].includes(user.role) && (
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogTrigger asChild>
 <Button data-testid="create-unit-button"className="bg-slate-900 hover:bg-slate-800:bg-zinc-200"><Plus className="w-4 h-4 mr-2"/>{t('units.newUnit')}</Button>
 </DialogTrigger>
 <DialogContent data-testid="create-unit-dialog">
 <DialogHeader><DialogTitle>{t('units.newUnit')}</DialogTitle></DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label htmlFor="project_id">{t('projects.title')}</Label>
 <Select value={selectedProjectForForm} onValueChange={(val) => {
 setSelectedProjectForForm(val);
 setFormData({ ...formData, project_id: val, building_id:""});
 }}>
 <SelectTrigger><SelectValue placeholder={t('units.selectProjectFirst')} /></SelectTrigger>
 <SelectContent>
 {projects.map((project) => (<SelectItem key={project.id} value={project.id}>{project.name}</SelectItem>))}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="building_id">{t('buildings.title')}</Label>
 <Select 
 value={formData.building_id} 
 onValueChange={(val) => setFormData({ ...formData, building_id: val })}
 disabled={!selectedProjectForForm}
 >
 <SelectTrigger data-testid="unit-building-select">
 <SelectValue placeholder={selectedProjectForForm ? t('units.selectBuilding') : t('units.selectProjectFirst')} />
 </SelectTrigger>
 <SelectContent>
 {buildings
 .filter(b => b.project_id === selectedProjectForForm)
 .map((building) => (<SelectItem key={building.id} value={building.id}>{building.name}</SelectItem>))}
 </SelectContent>
 </Select>
 {selectedProjectForForm && buildings.filter(b => b.project_id === selectedProjectForForm).length === 0 && (
 <p className="text-sm text-orange-600 mt-1">{t('units.noBuildingsInProject')}</p>
 )}
 </div>
 <div>
 <Label htmlFor="code">{t('units.unitCode')}</Label>
 <Input id="code"data-testid="unit-code-input"value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} placeholder="e.g., A-101"required />
 </div>
 <div>
 <Label htmlFor="unit_type">{t('units.unitType')}</Label>
 <Select value={formData.unit_type} onValueChange={(value) => setFormData({ ...formData, unit_type: value })}>
 <SelectTrigger><SelectValue /></SelectTrigger>
 <SelectContent>
 <SelectItem value="residential">{t('units.residential')}</SelectItem>
 <SelectItem value="commercial">{t('units.commercial')}</SelectItem>
 <SelectItem value="parking">{t('units.parking')}</SelectItem>
 <SelectItem value="storage">{t('units.storage')}</SelectItem>
 <SelectItem value="bicycle_parking">{t('units.bicycleParking')}</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="grid grid-cols-3 gap-4">
 <div><Label htmlFor="floor">{t('units.floor')}</Label><Input id="floor"data-testid="unit-floor-input"type="number"value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) })} required /></div>
 <div><Label htmlFor="bedrooms">{t('units.bedrooms')}</Label><Input id="bedrooms"data-testid="unit-bedrooms-input"type="number"value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) })} required /></div>
 <div><Label htmlFor="net_m2">{t('units.netArea')}</Label><Input id="net_m2"data-testid="unit-net-m2-input"type="number"step="0.1"value={formData.net_m2} onChange={(e) => setFormData({ ...formData, net_m2: parseFloat(e.target.value) })} required /></div>
 </div>
 <div>
 <Label htmlFor="floor_plan_url">{t('units.floorPlanUrl')}</Label>
 <Input 
 id="floor_plan_url"
 value={formData.floor_plan_url} 
 onChange={(e) => setFormData({ ...formData, floor_plan_url: e.target.value })} 
 placeholder="https://example.com/floorplan.pdf"
 />
 <p className="text-[11px] text-slate-500 mt-1">{t('units.floorPlanHint')}</p>
 </div>
 <Button type="submit"data-testid="submit-unit-button"className="w-full bg-slate-900 hover:bg-slate-800:bg-zinc-200">{t('units.newUnit')}</Button>
 </form>
 </DialogContent>
 </Dialog>
 )}
 </div>
 {canFilterAvailability && (
 <BedroomFilter value={selectedBedrooms} onChange={handleBedroomChange} options={bedroomOptions} resultCount={visibleUnitCount} loading={loading} />
 )}
 {loading ? (
 <div className="flex items-center justify-center h-64"><div className="text-sm text-slate-500">{t('common.loading')}</div></div>
 ) : (
 <div className="space-y-6">
 {bedroomFilterActive && visibleUnitCount === 0 && (
 <p data-testid="units-bedroom-empty-state" role="status" className="py-12 text-center text-sm text-slate-500">
 {t('units.bedroomFilter.noMatches', { count: Number(selectedBedrooms) })}
 </p>
 )}
 {Object.values(groupedUnits).map((projectGroup) => {
 const buildingsWithUnits = Object.values(projectGroup.buildings).filter(b => b.units.length > 0);
 if (buildingsWithUnits.length === 0) return null;
 
 const isExpanded = bedroomFilterActive
 ? filteredExpandedProjects[projectGroup.project.id] !== false : expandedProjects[projectGroup.project.id];
 
 return (
 <div key={projectGroup.project.id} className="space-y-4">
 {/* Project Header - Clickable */}
 <button
 type="button"
 data-testid={`units-project-toggle-${projectGroup.project.id}`}
 aria-expanded={Boolean(isExpanded)}
 className="w-full text-left flex flex-wrap items-center gap-3 pb-2 border-b-2 border-blue-500 cursor-pointer hover:bg-white/80 transition-colors p-2 rounded-t-lg"
 onClick={() => toggleProject(projectGroup.project.id)}
 >
 {isExpanded ? (
 <ChevronDown className="w-5 h-5 text-blue-600"/>
 ) : (
 <ChevronRight className="w-5 h-5 text-blue-600"/>
 )}
 <Building2 className="w-6 h-6 text-blue-600"/>
 <div className="flex-1 min-w-0 break-words">
 <h2 data-testid={`units-project-name-${projectGroup.project.id}`} className="text-xl font-bold text-slate-900">{projectGroup.project.name}</h2>
 <p data-testid={`units-project-location-${projectGroup.project.id}`} className="text-sm text-slate-500">{projectGroup.project.code} • {projectGroup.project.city}</p>
 </div>
 <div data-testid={`units-project-count-${projectGroup.project.id}`} className="w-full sm:w-auto text-sm text-slate-500">
 {bedroomFilterActive ? t('units.bedroomFilter.results', { count: buildingsWithUnits.reduce((total, b) => total + b.units.length, 0) }) : <>
 {buildingsWithUnits.reduce((total, b) => total + b.units.filter(u => u.unit_type === 'residential' || u.unit_type === 'commercial').length, 0)} {t('units.units')} • {buildingsWithUnits.reduce((total, b) => total + b.units.filter(u => u.unit_type === 'parking' || u.unit_type === 'storage' || u.unit_type === 'bicycle_parking').length, 0)} {t('units.parkingStorage')}
 </>}
 </div>
 </button>

 {/* Area Summary Statistics */}
 {isExpanded && !bedroomFilterActive && (() => {
 const allUnits = buildingsWithUnits.flatMap(b => b.units);
 
 // For contractors, show same tile layout as owner but without availability status
 if (user.role ==="contractor") {
 return (
 <div className="space-y-4 mb-4">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
 {allUnits.map((unit) => (
 <Card key={unit.id} className="">
 <CardContent className="p-4">
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center gap-2">
 {unit.unit_type === 'commercial'
 ? <Briefcase className="w-5 h-5 text-blue-600"/>
 : <KeyRound className="w-5 h-5 text-purple-600"/>
 }
 <div>
 <p className="font-bold text-slate-900">{unit.code}</p>
 <p className="text-[11px] text-slate-500">Floor {unit.floor}</p>
 </div>
 </div>
 </div>
 <div className="space-y-1 text-sm">
 <div className="flex justify-between">
 <span className="text-sm text-slate-500">Bedrooms:</span>
 <span className="font-medium text-slate-900">{unit.bedrooms ?? "—"}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-sm text-slate-500">Area:</span>
 <span className="font-medium text-slate-900">{unit.net_m2 ?? unit.area ?? "—"} m²</span>
 </div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>
 );
 }
 
 // Residential calculations
 const residentialUnits = allUnits.filter(u => u.unit_type === 'residential');
 const totalResM2 = residentialUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const soldResUnits = residentialUnits.filter(u => u.status === 'sold');
 const soldResM2 = soldResUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const unsoldResUnits = residentialUnits.filter(u => u.status !== 'sold');
 const unsoldResM2 = unsoldResUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 
 // Commercial calculations
 const commercialUnits = allUnits.filter(u => u.unit_type === 'commercial');
 const totalComM2 = commercialUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const soldComUnits = commercialUnits.filter(u => u.status === 'sold');
 const soldComM2 = soldComUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const unsoldComUnits = commercialUnits.filter(u => u.status !== 'sold');
 const unsoldComM2 = unsoldComUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 
 // Parking & Storage calculations
 const parkingUnits = allUnits.filter(u => u.unit_type === 'parking' || u.unit_type === 'bicycle_parking');
 const storageUnits = allUnits.filter(u => u.unit_type === 'storage');
 const totalParkingM2 = parkingUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const totalStorageM2 = storageUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const soldParkingUnits = parkingUnits.filter(u => u.status === 'sold');
 const soldStorageUnits = storageUnits.filter(u => u.status === 'sold');
 const soldParkingM2 = soldParkingUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 const soldStorageM2 = soldStorageUnits.reduce((sum, unit) => sum + (unit.net_m2 || 0), 0);
 
 return (
 <div className="space-y-4 mb-4">
 {/* Residential Row */}
 <div>
 <h3 className="text-sm font-semibold text-[#334155] mb-2 flex items-center gap-2">
 <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">{t('units.residential')}</span>
 </h3>
 <div className="grid grid-cols-3 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('common.total')}</div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{totalResM2.toFixed(0)} m²</div>
 <div className="text-xs text-slate-600 mt-1">{residentialUnits.length} {t('units.units')}</div>
 </CardContent>
 </Card>
 
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('units.sold')}</div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{soldResM2.toFixed(0)} m²</div>
 <div className="text-xs text-slate-600 mt-1">{soldResUnits.length} {t('units.units')} • {totalResM2 > 0 ? ((soldResM2 / totalResM2) * 100).toFixed(1) : 0}%</div>
 </CardContent>
 </Card>
 
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('units.unsold')}</div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{unsoldResM2.toFixed(0)} m²</div>
 <div className="text-xs text-slate-600 mt-1">{unsoldResUnits.length} {t('units.units')} • {totalResM2 > 0 ? ((unsoldResM2 / totalResM2) * 100).toFixed(1) : 0}%</div>
 </CardContent>
 </Card>
 </div>
 </div>
 
 {/* Commercial Row */}
 <div>
 <h3 className="text-sm font-semibold text-[#334155] mb-2 flex items-center gap-2">
 <span className="px-2 py-1 bg-blue-500/10 text-blue-700 rounded text-xs">{t('units.commercial')}</span>
 </h3>
 <div className="grid grid-cols-3 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('common.total')}</div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{totalComM2.toFixed(0)} m²</div>
 <div className="text-xs text-slate-600 mt-1">{commercialUnits.length} {t('units.units')}</div>
 </CardContent>
 </Card>
 
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('units.sold')}</div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{soldComM2.toFixed(0)} m²</div>
 <div className="text-xs text-slate-600 mt-1">{soldComUnits.length} {t('units.units')} • {totalComM2 > 0 ? ((soldComM2 / totalComM2) * 100).toFixed(1) : 0}%</div>
 </CardContent>
 </Card>
 
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('units.unsold')}</div>
 <div className="text-2xl font-semibold tabular-nums text-slate-900">{unsoldComM2.toFixed(0)} m²</div>
 <div className="text-xs text-slate-600 mt-1">{unsoldComUnits.length} {t('units.units')} • {totalComM2 > 0 ? ((unsoldComM2 / totalComM2) * 100).toFixed(1) : 0}%</div>
 </CardContent>
 </Card>
 </div>
 </div>
 
 {/* Parking & Storage Row */}
 {(parkingUnits.length > 0 || storageUnits.length > 0) && (
 <div className="grid grid-cols-2 gap-4">
 {parkingUnits.length > 0 && (
 <div>
 <h3 className="text-sm font-semibold text-[#334155] mb-2 flex items-center gap-2">
 <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">{t('units.parking')}</span>
 </h3>
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-slate-500 font-semibold mb-1">{t('common.total')}</div>
 <div className="text-2xl font-bold text-slate-900">{parkingUnits.length} {t('units.spaces')}</div>
 <div className="text-xs text-slate-600 mt-1">{totalParkingM2.toFixed(0)} m² • {soldParkingUnits.length} {t('units.sold').toLowerCase()}</div>
 </CardContent>
 </Card>
 </div>
 )}
 {storageUnits.length > 0 && (
 <div>
 <h3 className="text-sm font-semibold text-[#334155] mb-2 flex items-center gap-2">
 <span className="px-2 py-1 bg-stone-200 text-stone-700 rounded text-xs">{t('units.storage')}</span>
 </h3>
 <Card className="">
 <CardContent className="pt-6">
 <div className="text-sm text-stone-900 font-semibold mb-1">{t('common.total')}</div>
 <div className="text-2xl font-bold text-slate-900">{storageUnits.length} {t('units.spaces')}</div>
 <div className="text-xs text-slate-600 mt-1">{totalStorageM2.toFixed(0)} m² • {soldStorageUnits.length} {t('units.sold').toLowerCase()}</div>
 </CardContent>
 </Card>
 </div>
 )}
 </div>
 )}
 </div>
 );
 })()}

 {/* Buildings and Units - Collapsible */}
 {isExpanded && buildingsWithUnits.map((buildingGroup) => {
 const apartmentUnits = buildingGroup.units.filter(u => u.unit_type === 'residential' || u.unit_type === 'commercial');
 const facilityUnits = buildingGroup.units.filter(u => u.unit_type === 'parking' || u.unit_type === 'storage' || u.unit_type === 'bicycle_parking');
 
 return (
 <div key={buildingGroup.building.id} className="space-y-3">
 <h3 data-testid={`units-building-name-${buildingGroup.building.id}`} className="text-lg font-semibold text-[#334155] ml-4 break-words">
 {buildingGroup.building.name}
 </h3>
 
 {/* Apartments & Commercial */}
 {apartmentUnits.length > 0 && (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
 {apartmentUnits.map((unit) => (
 <Card key={unit.id} data-testid={`unit-card-${unit.id}`} className="cursor-pointer min-w-0 transition-shadow hover:shadow-md" role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openUnitDetail(unit); } }} onClick={() => openUnitDetail(unit)}>
 <CardContent className="p-4">
 <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
 <div className="flex items-center gap-2 min-w-0">
 {unit.unit_type === 'commercial'
 ? <Briefcase className="w-5 h-5 text-blue-600"/>
 : <KeyRound className="w-5 h-5 text-purple-600"/>
 }
 <div className="min-w-0 break-words">
 <p data-testid={`unit-code-${unit.id}`} className="font-bold text-slate-900">{unit.code}</p>
 <p data-testid={`unit-floor-${unit.id}`} className="text-[11px] text-slate-500">{t('units.floor')} {unit.floor}</p>
 </div>
 </div>
 {unit.is_rented ? (
 <Badge data-testid={`unit-status-${unit.id}`} variant="outline"className="bg-violet-500/10 text-violet-700 border-purple-300">
 {t('units.rented')}
 </Badge>
 ) : (
 <Badge data-testid={`unit-status-${unit.id}`} variant="outline"className={{ available:"bg-emerald-500/10 text-emerald-700 border-green-200/60", hold:"bg-amber-500/10 text-amber-700 border-orange-200/60", reserved:"bg-blue-500/10 text-blue-700 border-blue-200/60", sold:"bg-white/60 text-slate-800 border-slate-200"}[unit.status]}>{t(`units.${unit.status}`, { defaultValue: unit.status })}</Badge>
 )}
 </div>
 <div className="space-y-1 text-sm">
 <div className="flex justify-between gap-2"><span className="text-sm text-slate-500">{t('units.bedrooms')}:</span><span data-testid={`unit-bedrooms-${unit.id}`} className="font-medium text-slate-900">{unit.bedrooms}</span></div>
 <div className="flex justify-between gap-2"><span className="text-sm text-slate-500">{t('units.area')}:</span><span data-testid={`unit-area-${unit.id}`} className="font-medium text-slate-900">{unit.net_m2} m²</span></div>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}
 
 {/* Parking & Storage - Separate Section */}
 {facilityUnits.length > 0 && (
 <div className="mt-4">
 <div className="flex items-center gap-2 mb-3 ml-2">
 <Car className="w-4 h-4 text-amber-600"/>
 <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{t('units.parkingStorage')}</span>
 </div>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
 {facilityUnits.map((unit) => (
 <Card key={unit.id} data-testid={`unit-card-${unit.id}`} className="cursor-pointer"onClick={() => openUnitDetail(unit)}>
 <CardContent className="p-3">
 <div className="flex items-center gap-2 mb-1">
 {unit.unit_type === 'storage' ? (
 <Warehouse className="w-3.5 h-3.5 text-stone-500"/>
 ) : unit.unit_type === 'bicycle_parking' ? (
 <Bike className="w-3.5 h-3.5 text-amber-600"/>
 ) : (
 <Car className="w-3.5 h-3.5 text-amber-600"/>
 )}
 <p className="font-semibold text-slate-800 text-sm">{unit.code}</p>
 </div>
 <div className="flex items-center justify-between">
 <span className="text-[11px] text-slate-500">{unit.net_m2} m²</span>
 <Badge variant="outline"className={`text-[10px] px-1.5 py-0 ${
 unit.status === 'sold' ? 'bg-white/60 text-slate-600 border-slate-200' : 'bg-green-50/80 text-green-700 border-green-200/60'
 }`}>{unit.status}</Badge>
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
 })}
 </div>
 )}
 </div>
 {/* Unit detail with Documents */}
 <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
 <DialogContent data-testid="unit-detail-dialog" className="max-w-4xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogDescription data-testid="unit-detail-description" className="sr-only">
 {t('units.unitCode')}: {selectedUnit?.code}. {t('units.floor')}: {selectedUnit?.floor}. {t('units.bedrooms')}: {selectedUnit?.bedrooms}.
 </DialogDescription>
 <div className="flex items-center justify-between">
 <DialogTitle>Unit Details • {selectedUnit?.code}</DialogTitle>
 {["owner","management","sales"].includes(user.role) && (
 <Button 
 size="sm"
 variant="outline"
 onClick={(e) => {
 e.stopPropagation();
 openEditDialog(selectedUnit);
 setDetailOpen(false);
 }}
 >
 Edit Unit
 </Button>
 )}
 </div>
 </DialogHeader>
 <Tabs defaultValue="overview">
 <TabsList>
 <TabsTrigger value="overview">Overview</TabsTrigger>
 <TabsTrigger value="documents">Documents</TabsTrigger>
 </TabsList>
 <TabsContent value="overview"className="space-y-6">
 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-2">
 <div className="text-[11px] text-slate-500 uppercase">Unit Info</div>
 <div className="text-sm text-[#334155] space-y-1">
 <div className="flex justify-between"><span className="text-slate-600">Code:</span><span className="font-medium">{selectedUnit?.code}</span></div>
 <div className="flex justify-between"><span className="text-slate-600">Floor:</span><span className="font-medium">{selectedUnit?.floor}</span></div>
 <div className="flex justify-between"><span className="text-slate-600">Bedrooms:</span><span className="font-medium">{selectedUnit?.bedrooms}</span></div>
 <div className="flex justify-between"><span className="text-slate-600">Area:</span><span className="font-medium">{selectedUnit?.net_m2} m²</span></div>
 <div className="flex justify-between">
 <span className="text-slate-600">Status:</span>
 {selectedUnit?.is_rented ? (
 <Badge variant="outline"className="bg-violet-500/10 text-violet-700 border-purple-300">
 Rented
 </Badge>
 ) : (
 <Badge variant="outline"className={statusColors[selectedUnit?.status]}>{selectedUnit?.status}</Badge>
 )}
 </div>
 {selectedUnit?.is_rented && selectedUnit?.rental_info && (
 <div className="mt-2 p-2 bg-purple-50/80 border border-purple-200 rounded">
 <div className="text-xs font-semibold text-purple-900 mb-1">Rental Details:</div>
 <div className="text-xs text-purple-800 space-y-1">
 <div>Tenant: {selectedUnit.rental_info.tenant_name || 'N/A'}</div>
 <div>Lease End: {selectedUnit.rental_info.lease_end_date ? new Date(selectedUnit.rental_info.lease_end_date).toLocaleDateString() : 'N/A'}</div>
 </div>
 </div>
 )}
 </div>
 </div>

 {selectedUnit?.status ==="reserved"|| selectedUnit?.status ==="sold"? (
 <div className="space-y-2">
 <div className="text-[11px] text-slate-500 uppercase">Reservation Info</div>
 {loadingReservation ? (
 <div className="text-sm text-slate-500">Loading...</div>
 ) : unitReservation ? (
 <div className="text-sm text-[#334155] space-y-1">
 <div className="flex justify-between"><span className="text-slate-600">Buyer:</span><span className="font-medium">{unitReservation.buyer_name}</span></div>
 <div className="flex justify-between"><span className="text-slate-600">Phone:</span><span className="font-medium">{unitReservation.buyer_phone}</span></div>
 <div className="flex justify-between"><span className="text-slate-600">Email:</span><span className="font-medium">{unitReservation.buyer_email}</span></div>
 {typeof unitReservation.contract_value === 'number' && (
 <div className="flex justify-between"><span className="text-slate-600">Contract Value:</span><span className="font-medium">€{unitReservation.contract_value.toLocaleString()}</span></div>
 )}
 </div>
 ) : (
 <div className="text-sm text-slate-500">No reservation data found</div>
 )}
 </div>
 ) : (
 <div className="space-y-2">
 <div className="text-[11px] text-slate-500 uppercase">Floor Plan</div>
 <div className="text-sm text-[#334155]">
 {selectedUnit?.floor_plan_url ? (
 <div className="space-y-2">
 <a 
 href={selectedUnit.floor_plan_url} 
 target="_blank"
 rel="noopener noreferrer"
 className="text-blue-600 hover:underline block"
 >
 View Floor Plan →
 </a>
 {(selectedUnit.floor_plan_url.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
 <img 
 src={selectedUnit.floor_plan_url} 
 alt="Floor plan preview"
 className="w-full rounded border"
 />
 )}
 </div>
 ) : (
 <span className="text-slate-500 italic">No floor plan uploaded</span>
 )}
 </div>
 </div>
 )}
 </div>

 {unitReservation && unitReservation.payment_plan && unitReservation.payment_plan.length > 0 && (
 <div className="space-y-2">
 <div className="text-[11px] text-slate-500 uppercase">Payment Schedule</div>
 <div className="border border-slate-200 rounded-sm overflow-hidden">
 <table className="w-full text-sm">
 <thead className="bg-white/50">
 <tr>
 <th className="text-left p-2 text-slate-600 font-medium">Amount</th>
 <th className="text-left p-2 text-slate-600 font-medium">Due Date</th>
 <th className="text-left p-2 text-slate-600 font-medium">Status</th>
 <th className="text-left p-2 text-slate-600 font-medium">Paid At</th>
 </tr>
 </thead>
 <tbody>
 {unitReservation.payment_plan.map((payment, idx) => (
 <tr key={idx} className="border-t border-white/40">
 <td className="p-2">€{typeof payment.amount === 'number' ? payment.amount.toLocaleString() : payment.amount}</td>
 <td className="p-2">{payment.due_date}</td>
 <td className="p-2">
 <Badge 
 variant="outline"
 className={
 payment.status ==="PAID"?"bg-emerald-500/10 text-emerald-700 border-green-200/60":
 payment.status ==="OVERDUE"?"bg-rose-500/10 text-rose-700 border-red-200/60":
"bg-white/60 text-slate-800 border-slate-200"
 }
 >
 {payment.status}
 </Badge>
 </td>
 <td className="p-2">{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() :"-"}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 )}
 </TabsContent>
 <TabsContent value="documents">
 {selectedUnit && (
 <EntityDocumentsTab user={user} entityType="unit"entityId={selectedUnit.id} />
 )}
 </TabsContent>
 </Tabs>
 <div className="flex gap-3 pt-4 border-t mt-4">
 {user.role ==="owner"|| user.role ==="management"? (
 <Button
 onClick={async () => {
 if (window.confirm("Are you sure you want to delete this unit?")) {
 try {
 await axios.delete(`/units/${selectedUnit.id}`);
 toast.success(t("messages.saveSuccess"));
 setDetailOpen(false);
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail ||"Failed to delete unit");
 }
 }
 }}
 variant="destructive"
 className="w-full"
 type="button"
 >
 Delete Unit
 </Button>
 ) : null}
 </div>
 </DialogContent>
 </Dialog>

 {/* Edit Unit Dialog */}
 <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
 <DialogContent className="max-w-md">
 <DialogHeader>
 <DialogTitle>Edit Unit • {selectedUnit?.code}</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleEditSubmit} className="space-y-4">
 <div>
 <Label htmlFor="edit_code">Unit Code *</Label>
 <Input
 id="edit_code"
 value={editFormData.code}
 onChange={(e) => setEditFormData({ ...editFormData, code: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="edit_floor">Floor *</Label>
 <Input
 id="edit_floor"
 type="number"
 value={editFormData.floor}
 onChange={(e) => setEditFormData({ ...editFormData, floor: parseInt(e.target.value) })}
 required
 />
 </div>
 <div>
 <Label htmlFor="edit_unit_type">Type *</Label>
 <Select
 value={editFormData.unit_type}
 onValueChange={(value) => setEditFormData({ ...editFormData, unit_type: value })}
 >
 <SelectTrigger id="edit_unit_type">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="residential">{t('units.residential')}</SelectItem>
 <SelectItem value="commercial">{t('units.commercial')}</SelectItem>
 <SelectItem value="parking">{t('units.parking')}</SelectItem>
 <SelectItem value="storage">{t('units.storage')}</SelectItem>
 <SelectItem value="bicycle_parking">{t('units.bicycleParking')}</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="edit_bedrooms">Bedrooms *</Label>
 <Input
 id="edit_bedrooms"
 type="number"
 value={editFormData.bedrooms}
 onChange={(e) => setEditFormData({ ...editFormData, bedrooms: parseInt(e.target.value) })}
 required
 />
 </div>
 <div>
 <Label htmlFor="edit_net_m2">Net Area (m²) *</Label>
 <Input
 id="edit_net_m2"
 type="number"
 step="0.01"
 value={editFormData.net_m2}
 onChange={(e) => setEditFormData({ ...editFormData, net_m2: parseFloat(e.target.value) })}
 required
 />
 </div>
 <div className="flex gap-2">
 <Button type="submit"className="flex-1 bg-slate-900 hover:bg-slate-800:bg-zinc-200">
 Save Changes
 </Button>
 <Button type="button"variant="outline"onClick={() => setEditDialogOpen(false)}>
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default UnitsPage;
